import React, { FC, forwardRef, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import filterPropsByComponent from 'ia-react-core/~/utils/components/_filterPropsByComponent';
import requiredValidator from '../../../validators/requiredValidator';
import Control from '../Control';
import { DropdownControlComposedProps, DropdownControlDefaultProps, DropdownControlValidationProps } from './DropdownControl';

const withControl = <Value, StateValue, OptionValue>(WrappedComponent: FC<DropdownControlComposedProps<Value, StateValue, OptionValue>>) => {
  const WithControl = forwardRef<HTMLInputElement, DropdownControlComposedProps<Value, StateValue, OptionValue>>(({
    required, recommended, options, readOnly, textOnly, defaultValue, defaultToUniqueOptionIfRequired, defaultValidators, ...props
  }, ref) => {
    const newDefaultValidators = useMemo(() => ({
      required: requiredValidator,
      recommended: { validator: requiredValidator, type: 'warning' },
      ...defaultValidators,
    }), [defaultValidators]);

    const resolvedDefaultValue = useMemo(() => {
      if (required && options?.length === 1 && defaultToUniqueOptionIfRequired !== false) return options[0].value as unknown as Value;
      return defaultValue;
    }, [defaultToUniqueOptionIfRequired, defaultValue, options, required]);

    const TypedControl = Control<Value, StateValue, HTMLInputElement, DropdownControlValidationProps>;

    return (
      <TypedControl
        {...filterPropsByComponent(props, TypedControl)}
        required={required}
        recommended={recommended}
        defaultValidators={newDefaultValidators}
        defaultValue={resolvedDefaultValue}
        readOnly={readOnly}
        textOnly={textOnly}
      >
        {({
          id, value, isValid, hasWarning, hasCaution, isTouched, errorMessagesId, onValueChange: onValueChangeCallback, onTouched: onTouchedCallback, isUnionControlGroup,
        }) => (
          <WrappedComponent
            id={id}
            data-control
            {...props}
            options={options}
            value={value}
            isValid={isValid}
            hasWarning={hasWarning}
            hasCaution={hasCaution}
            isTouched={isTouched}
            errorMessagesId={errorMessagesId}
            onValueChangeCallback={onValueChangeCallback}
            onTouchedCallback={onTouchedCallback}
            isUnionControlGroup={isUnionControlGroup}
            readOnly={readOnly}
            textOnly={textOnly}
            required={required}
            ref={ref}
          />
        )}
      </TypedControl>
    );
  });

  WithControl.defaultProps = DropdownControlDefaultProps;

  return hoistNonReactStatics(WithControl, WrappedComponent);
};

export default withControl;
